import { CardProgram } from 'redux/programs/programsTypes'
import { ProgramStatusType, ProgramsType } from './types'

const CARD_PROGRAM_TYPE_TO_PROGRAMS_TYPE: Partial<
  Record<CardProgram['type'], ProgramsType>
> = {
  GRH: 'grh',
  MICROMOBILITY: 'micromobility',
  FLEX: 'flex',
  TRANSIT_PASS: 'transitPass',
  COUPON: 'coupon',
  REPORTED_USAGE: 'reportedUsage',
}

export const getProgramScopeId = (
  program: Pick<CardProgram, 'segment' | 'tag'>
): string | undefined => program.tag || program.segment

const programScopeEntry = (programData: CardProgram) => ({
  programId: programData.id,
  segmentId: programData.segment,
  tagId: programData.tag,
  scopeId: getProgramScopeId(programData),
})

export const computeLaunchGroupStatus = (
  statuses: (ProgramStatusType | undefined)[]
): ProgramStatusType => {
  const validStatuses = statuses.filter(Boolean) as ProgramStatusType[]

  if (validStatuses.some((status) => status === 'UPDATING')) {
    return 'UPDATING'
  }
  if (validStatuses.some((status) => status === 'ACTIVE')) {
    return 'ACTIVE'
  }
  if (validStatuses.some((status) => status === 'CANCELLING')) {
    return 'CANCELLING'
  }
  if (validStatuses.some((status) => status === 'PENDING')) {
    return 'PENDING'
  }

  return validStatuses[0] ?? 'PENDING'
}

export const superProgramStatus = (
  programData: CardProgram,
  oldStatus?: ProgramStatusType
): ProgramStatusType => {
  return computeLaunchGroupStatus([
    oldStatus,
    programData.status as ProgramStatusType,
  ])
}

export const createSegments = (
  programData: CardProgram,
  oldSegments: string[] = []
): string[] => {
  const scopeId = getProgramScopeId(programData)
  if (programData.status === 'CANCELLING') {
    return oldSegments
  }
  if (oldSegments.length === 0) {
    return scopeId ? [scopeId] : []
  }
  return scopeId ? [...new Set([...oldSegments, scopeId])] : oldSegments
}

export const createCancelledSegments = (
  programData: CardProgram,
  oldSegments: string[] = []
): string[] => {
  const scopeId = getProgramScopeId(programData)
  if (programData.status !== 'CANCELLING') {
    return oldSegments
  }
  if (oldSegments.length === 0) {
    return scopeId ? [scopeId] : []
  }
  return scopeId ? [...new Set([...oldSegments, scopeId])] : oldSegments
}

const resolveSuperProgramName = (
  segmentPrograms: CardProgram[]
): string | undefined => {
  const hasRunningProgram = segmentPrograms.some(
    (program) => program.status === 'ACTIVE' || program.status === 'UPDATING'
  )
  const pendingProgram = segmentPrograms.find(
    (program) => program.status === 'PENDING'
  )

  if (hasRunningProgram && pendingProgram?.superProgramName) {
    return pendingProgram.superProgramName
  }

  return segmentPrograms.find((program) => program.superProgramName)
    ?.superProgramName
}

const pickProgramForLaunchSegment = (
  programs: CardProgram[]
): CardProgram | null => {
  const updating = programs.find((program) => program.status === 'UPDATING')
  const active = programs.find((program) => program.status === 'ACTIVE')
  const pending = programs.find((program) => program.status === 'PENDING')
  return updating || active || pending || null
}

export const mergeProgramIntoLaunchGroup = (
  programGroups: Record<string, any>,
  programData: CardProgram,
  programType: string
) => {
  if (programGroups[programData.launchId]) {
    programGroups[programData.launchId] = {
      ...programGroups[programData.launchId],
      ...programData,
      superProgramName:
        programGroups[programData.launchId].superProgramName ||
        programData.superProgramName,
      currency:
        programGroups[programData.launchId].currency ||
        programData.currency ||
        'USD',
      description:
        programGroups[programData.launchId].description ||
        programData.description ||
        '',
      status: superProgramStatus(
        programData,
        programGroups[programData.launchId].status
      ),
      [programType]: programData,
      segments: createSegments(
        programData,
        programGroups[programData.launchId].segments
      ),
      cancelledSegments: createCancelledSegments(
        programData,
        programGroups[programData.launchId].cancelledSegments
      ),
      programTypes: [
        ...new Set([
          ...programGroups[programData.launchId].programTypes,
          programType,
        ]),
      ],
      programIds: {
        ...programGroups[programData.launchId].programIds,
        [programType]: programGroups[programData.launchId].programIds[
          programType
        ]
          ? [
              ...programGroups[programData.launchId].programIds[programType],
              programScopeEntry(programData),
            ]
          : [programScopeEntry(programData)],
      },
    }
    return
  }

  programGroups[programData.launchId] = {
    ...programData,
    currency: programData.currency || 'USD',
    status: superProgramStatus(programData),
    [programType]: programData,
    segments: createSegments(programData, []),
    cancelledSegments: createCancelledSegments(programData, []),
    programTypes: [programType],
    programIds: {
      [programType]: [programScopeEntry(programData)],
    },
  }
}

export const mergeCardProgramsByLaunch = (
  allCardPrograms: CardProgram[] | null,
  programGroups: Record<string, any>
) => {
  if (!allCardPrograms?.length) {
    return programGroups
  }

  const programsByLaunchTypeSegment: Record<
    string,
    Record<string, CardProgram[]>
  > = {}

  allCardPrograms.forEach((program) => {
    if (program.status === 'CANCELLED' || !program.launchId) {
      return
    }

    const programType = CARD_PROGRAM_TYPE_TO_PROGRAMS_TYPE[program.type]
    const scopeId = getProgramScopeId(program)
    if (!programType || !scopeId) {
      return
    }

    const segmentKey = `${programType}|${scopeId}`
    if (!programsByLaunchTypeSegment[program.launchId]) {
      programsByLaunchTypeSegment[program.launchId] = {}
    }
    if (!programsByLaunchTypeSegment[program.launchId][segmentKey]) {
      programsByLaunchTypeSegment[program.launchId][segmentKey] = []
    }
    programsByLaunchTypeSegment[program.launchId][segmentKey].push(program)
  })

  Object.values(programsByLaunchTypeSegment).forEach((launchPrograms) => {
    Object.entries(launchPrograms).forEach(([segmentKey, segmentPrograms]) => {
      const [programType] = segmentKey.split('|')
      const programData = pickProgramForLaunchSegment(segmentPrograms)
      if (!programData?.launchId) {
        return
      }
      const superProgramName = resolveSuperProgramName(segmentPrograms)
      mergeProgramIntoLaunchGroup(
        programGroups,
        superProgramName
          ? { ...programData, superProgramName }
          : programData,
        programType
      )
    })
  })

  return programGroups
}

const collectStatusesByLaunchId = (
  statusesByLaunchId: Record<string, ProgramStatusType[]>,
  launchId: string | undefined,
  status: ProgramStatusType | undefined
) => {
  if (!launchId || !status || status === 'CANCELLED') {
    return
  }

  if (!statusesByLaunchId[launchId]) {
    statusesByLaunchId[launchId] = []
  }
  statusesByLaunchId[launchId].push(status)
}

export const applyLaunchGroupStatuses = (
  programGroups: Record<string, any>,
  allCardPrograms: CardProgram[] | null,
  qtfPrograms: object | null
) => {
  const statusesByLaunchId: Record<string, ProgramStatusType[]> = {}

  allCardPrograms?.forEach((program) => {
    collectStatusesByLaunchId(
      statusesByLaunchId,
      program.launchId,
      program.status as ProgramStatusType
    )
  })

  if (qtfPrograms) {
    Object.values(qtfPrograms as Record<string, any>).forEach((programObject) => {
      ;[programObject?.active, programObject?.nextMonth].forEach(
        (programData) => {
          collectStatusesByLaunchId(
            statusesByLaunchId,
            programData?.launchId,
            programData?.status
          )
        }
      )
    })
  }

  Object.entries(programGroups).forEach(([launchId, programGroup]) => {
    programGroup.status = computeLaunchGroupStatus(
      statusesByLaunchId[launchId] ?? [programGroup.status]
    )
  })

  return programGroups
}
